import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Loader2, Truck } from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Reveal } from "@/components/shared/Reveal";
import { DriverFilterBar } from "@/components/drivers/DriverFilterBar";
import { DriverCard } from "@/components/drivers/DriverCard";
import { DriverRequestModal } from "@/components/drivers/DriverRequestModal";
import { DriverSignupModal } from "@/components/drivers/DriverSignupModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DriverDirectory() {
  const [drivers, setDrivers] = useState(null);
  const [filters, setFilters] = useState({ state: "", cdl_class: "", trailer_type: "" });
  const [requestDriver, setRequestDriver] = useState(null);
  const [signupOpen, setSignupOpen] = useState(false);

  useEffect(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    axios.get(`${API}/drivers`, { params }).then(({ data }) => setDrivers(data.drivers)).catch(() => setDrivers([]));
  }, [filters]);

  return (
    <div data-testid="driver-directory-page" className="min-h-screen bg-white">
      <Header />

      <section className="pt-36 pb-16 bg-rr-navy relative overflow-hidden">
        <div className="grain-overlay" />
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center relative">
          <Reveal>
            <div className="w-14 h-14 rounded-2xl bg-rr-orange/10 text-rr-orangeLight flex items-center justify-center mx-auto mb-5">
              <Truck size={24} />
            </div>
            <div className="text-xs font-bold tracking-[0.22em] uppercase text-rr-orangeLight mb-4">Drivers On Demand</div>
            <h1 data-testid="driver-directory-title" className="font-heading text-3xl md:text-5xl font-extrabold text-white tracking-tight leading-tight">
              Find a Driver, Right When You Need One
            </h1>
            <p className="text-white/60 mt-4 max-w-xl mx-auto">
              A live directory of relief drivers, owner operators, and CDL holders ready for their next assignment.
            </p>
            <button
              onClick={() => setSignupOpen(true)}
              data-testid="driver-directory-join-button"
              className="mt-8 inline-flex items-center gap-2 bg-rr-orange text-white font-bold px-8 py-4 rounded-full hover:bg-white hover:text-rr-navy transition-all hover:-translate-y-1"
            >
              Join as a Driver
            </button>
            <Link to="/driver-portal" data-testid="driver-directory-portal-link" className="block mt-5 text-sm font-bold text-white/60 hover:text-white transition-colors">
              Already signed in? Go to your Driver Portal →
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="py-14 bg-rr-surface">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <DriverFilterBar filters={filters} setFilters={setFilters} />

          {drivers === null ? (
            <div className="flex justify-center py-20"><Loader2 size={24} className="animate-spin text-rr-orange" /></div>
          ) : drivers.length === 0 ? (
            <div data-testid="driver-directory-empty-state" className="text-center py-20">
              <p className="text-rr-muted font-semibold">No drivers match yet — be the first to join the directory!</p>
              <button onClick={() => setSignupOpen(true)} data-testid="driver-directory-empty-join-button" className="mt-4 text-rr-orange font-bold hover:underline">
                Join as a Driver →
              </button>
            </div>
          ) : (
            <div data-testid="driver-directory-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
              {drivers.map((d) => (
                <DriverCard key={d.id} driver={d} onRequest={setRequestDriver} />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
      <DriverRequestModal driver={requestDriver} onOpenChange={(open) => !open && setRequestDriver(null)} />
      <DriverSignupModal open={signupOpen} onOpenChange={setSignupOpen} />
    </div>
  );
}
